import { PublicationSheetWithId, ModelType } from '@livv/models';
import { getSheetLink } from '@livv/utils/helpers';
import { Box, Grid, SxProps, Typography } from '@mui/material';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { FC, useMemo } from 'react';
import { useSheetByNumber } from '../utils/findSheet';
import useAmplitude from '@utils/hooks/useAmplitude';
import { useBookSheetSlugs } from '@utils/hooks/useBookSlugs';
import { NavigationDirection, TrackingEventName } from '@utils/types/amplitude/events';

interface SheetLinkProps {
    bookId: string;
    bookPath: string;
    sheetId: string;
}

const paperSx: SxProps = {
    backgroundColor: 'background.paper',
    borderRadius: '8px 8px 0 0',
    p: 2,
};

const titleSx: SxProps = {
    alignItems: 'center',
    display: 'flex',
    fontWeight: 'medium',
};

const formatTitleLink = (sheet: Pick<PublicationSheetWithId, 'id' | 'number' | 'title'>): string =>
    `${sheet.number}. ${sheet.title}`;

const getSheetLinkProps = (
    isListEnd: boolean,
    listSiblingId: string,
    nodeSiblingSheet: PublicationSheetWithId,
    { bookId, bookPath, sheetId }: Record<string, string>,
): SheetLinkProps | null => {
    if (isListEnd) {
        return nodeSiblingSheet
            ? {
                  bookId,
                  bookPath: nodeSiblingSheet.keyword.path.join('/'),
                  sheetId: nodeSiblingSheet.id,
              }
            : null;
    }

    return listSiblingId !== sheetId
        ? {
              bookId,
              bookPath,
              sheetId: listSiblingId,
          }
        : null;
};

interface PreviousNextSheetLinks {
    sheets: PublicationSheetWithId[];
}

const PreviousNextSheetLinks: FC<PreviousNextSheetLinks> = ({ sheets }) => {
    const { t } = useTranslation('books');
    const { bookId, bookPath, sheetId } = useBookSheetSlugs();
    const list = sheets.map(({ id, number, title }) => ({ id, number, title }));
    const previousPublishedSheet = useSheetByNumber(list?.at(0)?.number as number, false);
    const nextPublishedSheet = useSheetByNumber(list?.at(-1)?.number as number, true);
    const { track } = useAmplitude();

    const handleNavigate = (id: string, direction: NavigationDirection) => {
        track({
            name: TrackingEventName.CONTENT_NAVIGATION_CLICKED,
            params: {
                document_type: ModelType.PUBLICATION_SHEET,
                navigation_direction: direction,
                target_document_id: id,
            },
        });
    };

    const { isFirst, isLast, next, previous } = useMemo(() => {
        const sheetIndex = list.findIndex(({ id }) => id === sheetId);
        const previousSheet = list[Math.max(0, sheetIndex - 1)];
        const nextSheet = list[Math.min(list.length - 1, sheetIndex + 1)];

        return {
            isFirst: sheetIndex === 0,
            isLast: sheetIndex === list.length - 1,
            next: nextSheet,
            previous: previousSheet,
        };
    }, [list, sheetId]);

    const previousSheetProps = previousPublishedSheet
        ? getSheetLinkProps(isFirst, previous.id, previousPublishedSheet, {
              bookId,
              bookPath,
              sheetId,
          })
        : null;
    const nextSheetProps = nextPublishedSheet
        ? getSheetLinkProps(isLast, next.id, nextPublishedSheet, {
              bookId,
              bookPath,
              sheetId,
          })
        : null;

    return (
        <Box sx={{ mb: 2, mt: 8 }}>
            <Grid spacing={3} container>
                <Grid sm={6} xs={12} item>
                    {previousSheetProps && (
                        <Link
                            href={getSheetLink(previousSheetProps)}
                            onClick={() => handleNavigate(previousSheetProps.sheetId, 'previous')}
                        >
                            <Box sx={paperSx}>
                                <Typography variant="body2" gutterBottom>
                                    {t('previousText')}
                                    {isFirst && (
                                        <Box component="span" sx={{ color: 'grey.400' }}>
                                            {t('previousNode')}
                                        </Box>
                                    )}
                                </Typography>
                                <Typography sx={titleSx} variant="body2">
                                    <img alt="arrow-left" src="/assets/arrow_left_black.svg" />
                                    <Box component="span" sx={{ ml: 2 }}>
                                        {formatTitleLink(
                                            isFirst && previousPublishedSheet
                                                ? previousPublishedSheet
                                                : previous,
                                        )}
                                    </Box>
                                </Typography>
                            </Box>
                        </Link>
                    )}
                </Grid>
                {nextSheetProps && (
                    <Grid sm={6} xs={12} item>
                        <Link
                            href={getSheetLink(nextSheetProps)}
                            onClick={() => handleNavigate(nextSheetProps.sheetId, 'next')}
                        >
                            <Box sx={paperSx}>
                                <Typography variant="body2" gutterBottom>
                                    {t('nextText')}
                                    {isLast ? (
                                        <Box component="span" sx={{ color: 'grey.400' }}>
                                            {t('nextNode')}
                                        </Box>
                                    ) : null}
                                </Typography>
                                <Typography sx={titleSx} variant="body2">
                                    <Box component="span" sx={{ mr: 2 }}>
                                        {formatTitleLink(
                                            isLast && nextPublishedSheet
                                                ? nextPublishedSheet
                                                : next,
                                        )}
                                    </Box>
                                    <img alt="arrow-right" src="/assets/arrow_right_black.svg" />
                                </Typography>
                            </Box>
                        </Link>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default PreviousNextSheetLinks;
